import { Dispatch, SetStateAction, useState } from "react";
import styles from "./Placeholder.module.css";
import {
    Card,
    Collapsible,
    Icon,
    Label,
    Select,
    TextField,
} from "@shopify/polaris";
import { DeleteIcon, DiscountIcon } from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import RenderSelectedProduct2 from "../../../components/RenderSelectedProduct2/RenderSelectedProduct2";
import { BundleTypes } from "../constants";
import { CollectionsType, ProductObj, SubStepType } from "../Types";
import RenderSelectedCollections2 from "../../../components/RenderSelectedCollections2/RenderSelectedCollections2";
import ShopifyNativeError from "../../../components/ShopifyNativeError/ShopifyNativeError";

interface PlaceholderSubStepProps {
    subStep: SubStepType;
    subStepIndex: number;
    stepIndex: number;
    handleEditSubStep: <K extends keyof SubStepType>(
        stepIdx: number,
        subStepIdx: number,
        key: K | "delete",
        value?: SubStepType[K]
    ) => void;
    isMobile: boolean;
    expandedSubStep: number;
    setExpandedSubStep: Dispatch<SetStateAction<number>>;
}

const PlaceholderSubStep = ({
    subStep,
    subStepIndex,
    stepIndex,
    handleEditSubStep,
    isMobile,
    expandedSubStep,
    setExpandedSubStep,
}: PlaceholderSubStepProps) => {
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hasInteractedProduct, setHasInteractedProduct] = useState(false);
    const [errors, setErrors] = useState<{
        step_label: string;
        products: string;
        collections: string;
    }>({
        step_label: "",
        products: "",
        collections: "",
    });

    const isExpanded = expandedSubStep === subStepIndex;

    return (
        <Card padding={"300"} background={ "bg-surface-active" }>
            <div
                onClick={() => setExpandedSubStep((ps) => (ps === subStepIndex ? -1 : subStepIndex))}
                className={styles.customCardLabelWrapper}
                style={{
                    marginBottom: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                    <Icon source={DiscountIcon} />
                    <Label id={`substep_${subStepIndex}`}>
                        {subStep.step_label || `Category ${subStepIndex + 1}`}
                    </Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor={"pointer"} />
                    </div>
                </div>
                <span
                    className={styles.stepDragIcon}
                    style={{ color: "#9b9b9b" }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditSubStep(stepIndex, subStepIndex, "delete");
                    }}
                >
                    <Icon source={DeleteIcon} tone={"critical"} />
                </span>
            </div>
            <Collapsible
                open={isExpanded}
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
                id={`substep-collapsible-${subStepIndex}`}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        paddingTop: "10px",
                    }}
                >
                    <TextField
                        label={"Category Label"}
                        placeholder="e.g., Base Products, Add-ons, Extras"
                        autoComplete={"off"}
                        value={subStep.step_label}
                        onBlur={() => {
                            if (!hasInteracted) setHasInteracted(true);
                        }}
                        onChange={(val) => {
                            setErrors((ps) => ({
                                ...ps,
                                step_label: val.trim() === "" ? "Category label is required" : "",
                            }));
                            if (!hasInteracted) setHasInteracted(true);
                            handleEditSubStep(stepIndex, subStepIndex, "step_label", val);
                        }}
                        maxLength={80}
                        showCharacterCount
                        error={hasInteracted ? errors.step_label : undefined}
                    />

                    <Select
                        label={"Choose Products"}
                        options={[
                            {
                                label: "Specific Products",
                                value: "specific",
                            },
                            {
                                label: "Collection",
                                value: "collection",
                            },
                        ]}
                        onChange={(val) => {
                            handleEditSubStep(
                                stepIndex,
                                subStepIndex,
                                "product_selection",
                                val as "specific" | "collection"
                            );
                        }}
                        value={subStep?.product_selection || "specific"}
                    />

                    {subStep.product_selection === "specific" && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: 10,
                                paddingBottom: 20,
                            }}
                        >
                            <RenderSelectedProduct2
                                bundleType={BundleTypes.BB_PLACEHOLDER}
                                products={subStep.products || []}
                                submit={() => {
                                    if (!hasInteractedProduct) setHasInteractedProduct(true);
                                }}
                                isMobile={isMobile}
                                multiple={true}
                                handleFieldChange={(value: ProductObj[]) => {
                                    setErrors((ps) => ({
                                        ...ps,
                                        products:
                                            value?.length === 0
                                                ? "At least one product must be selected"
                                                : "",
                                    }));
                                    if (!hasInteractedProduct) setHasInteractedProduct(true);
                                    handleEditSubStep(stepIndex, subStepIndex, "products", value);
                                }}
                            />
                            {!!errors?.products?.length && hasInteractedProduct && (
                                <ShopifyNativeError
                                    error={errors.products}
                                    style={{ marginTop: 5, paddingLeft: 2 }}
                                />
                            )}
                        </div>
                    )}

                    {subStep.product_selection === "collection" && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: 10,
                                width: "100%",
                            }}
                        >
                            <RenderSelectedCollections2
                                collections={subStep.collections || []}
                                multiple={1}
                                submit={() => {
                                    if (!hasInteractedProduct) setHasInteractedProduct(true);
                                }}
                                handleFieldChange={(value: CollectionsType[]) => {
                                    setErrors((ps) => ({
                                        ...ps,
                                        collections:
                                            value?.length === 0
                                                ? "At least one collection must be selected"
                                                : "",
                                    }));
                                    if (!hasInteractedProduct) setHasInteractedProduct(true);
                                    handleEditSubStep(stepIndex, subStepIndex, "collections", value);
                                }}
                            />
                            {!!errors?.collections?.length && hasInteractedProduct && (
                                <ShopifyNativeError
                                    error={errors.collections}
                                    style={{ marginTop: 5, paddingLeft: 2 }}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Collapsible>
        </Card>
    );
};

export default PlaceholderSubStep;
